import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import { getFeature } from "@/FeatureFlag";
import { FreshpaintState } from "@/reducers";
import { ThemeProvider } from "@/theme/ThemeContext";
import {
  clearDarkModeFlagCache,
  DARK_MODE_FLAG_CACHE_KEY,
} from "@/theme/darkModeFlagCache";
import { userIsFPEmployee } from "@/util";

export function DarkModeGate({ children }: { children: React.ReactNode }) {
  // Select only booleans: the gate wraps the whole app, so a selector that
  // returns a new object would re-render every theme consumer on each dispatch.
  const loaded = useSelector(
    (state: FreshpaintState) => state.environments.loaded,
  );
  const resolvedFlag = useSelector(
    (state: FreshpaintState) =>
      state.environments.loaded &&
      getFeature(state.environments.accountMetadata, "darkMode"),
  );

  // Until account metadata loads, trust the last resolved flag so a flagged
  // user's dark theme doesn't flash light on every page load. The cache is
  // only set once the flag has resolved true, so an account that never had
  // the flag never renders dark. If the flag is turned off later, the user
  // gets at most one dark first render before the cache clears.
  const darkModeFlag = loaded
    ? resolvedFlag
    : localStorage.getItem(DARK_MODE_FLAG_CACHE_KEY) === "true";

  useEffect(() => {
    if (!loaded) return;
    if (resolvedFlag) {
      localStorage.setItem(DARK_MODE_FLAG_CACHE_KEY, "true");
    } else {
      clearDarkModeFlagCache();
    }
  }, [loaded, resolvedFlag]);

  const enabled =
    (userIsFPEmployee() || darkModeFlag) &&
    // The print page always opens in a new tab and is meant for paper and
    // PDFs, so it stays light.
    !window.location.pathname.startsWith("/ai-chat/print/");

  return <ThemeProvider enabled={enabled}>{children}</ThemeProvider>;
}
